// Datas civis (`yyyy-MM-dd`: vigência de vínculo, dia da ausência, disponibilidade, data do point-in-time) só para exibição.
// Diferente de um instante, uma data civil não pertence a um fuso: converter para o fuso do site (como `useDateFormatter().date`
// faz com instantes) deslocaria o dia. Luxon só exibe, nunca calcula (Parte 14.1): o dia é lido em UTC e formatado no idioma da UI.
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { browserTimeZone } from '../../../lib/dates/luxon';
import { todayIso } from '../../tenancy/components/forms';

/** `2026-10-02` → `2 de out. de 2026` (pt-BR) / `Oct 2, 2026` (en-US); valor inválido volta como veio. */
export function formatCivilDate(value: string, locale: string): string {
  const parsed = DateTime.fromISO(value, { zone: 'utc' }).setLocale(locale);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : value;
}

/** Formata uma data civil no idioma da UI; `—` quando não há (término em aberto). */
export function useCivilDate(): (value: string | null | undefined) => string {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  return useCallback((value) => (value ? formatCivilDate(value, locale) : '—'), [locale]);
}

/** Data civil de hoje no fuso dado (o do navegador quando não há fuso de site ativo). */
export function civilToday(zone: string | null): string {
  return todayIso(zone ?? browserTimeZone());
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string): boolean {
  return ISO_DATE.test(value);
}

/** Primeiros 8 caracteres de um identificador, para mostrar quem deu cada chave sem expor o UUID inteiro. */
export function shortId(value: string | null | undefined): string {
  return value ? value.slice(0, 8) : '—';
}
